/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import {
  INITIAL_CURRENT_PAGE,
  LIMIT,
  LIST_TYPES,
  INITIAL_TEXT,
  NOT_FOUND_ICON,
  NOT_FOUND_TEXT,
  INITIAL_DEFAULT_VALUE,
  SHORTCUT_PARAM
} from './constants'
import {
  PET_TYPES_OPTIONS,
  PET_GENDER_OPTIONS,
  PET_BREEDS_OPTIONS
} from '../../../utils/constants'
import { debounce } from '../../../utils/debounce'
import SideMenu from '../components/SideMenu'
import SearchInput from '../../../components/Form/SearchInput'
import Pagination from '../../../components/Pagination'
import TutorPagination from './components/TutorPagination'
import { Radio, Select } from '../../../components/Form'
import Alert from '../../../components/Form/Alert'
import { ReactComponent as FileCircleCheck } from '../../../assets/icons/file-circle-check.svg'
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus-icon.svg'
import * as S from './styles'
import HeaderPage from '../components/HeaderPage'
import ResultBox from '../components/ResultBox'
import { useGetTutor } from '../../../hooks/useGetUser'
import { useGetPet } from '../../../hooks/useGetPet'
import { getBreeds } from '../../../services/pet'
import NewTutorModal from '../components/Modals/NewTutorModal'
import SideMenuTemplate from '../templates/SideMenu'

const TutorsAndPets = () => {
  // Estados iniciais
  const [type, setType] = useState(LIST_TYPES.initial)
  const [textSearch, setTextSearch] = useState(INITIAL_TEXT)
  const [currentPage, setCurrentPage] = useState(INITIAL_CURRENT_PAGE)
  const [isFirstRender, setIsFirstRender] = useState(true)
  const [breedType, setBreedType] = useState(INITIAL_DEFAULT_VALUE)
  const [gender, setGender] = useState(INITIAL_DEFAULT_VALUE)
  const [breedId, setBreedId] = useState(INITIAL_DEFAULT_VALUE)
  const [breedsList, setBreedsList] = useState(INITIAL_DEFAULT_VALUE)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const searchInputRef = useRef(null)

  // Hooks personalizados para obter dados
  const {
    getTutor,
    user,
    loading: tutorsLoading,
    reset: resetTutors,
    total: totalTutors
  } = useGetTutor()

  const {
    getPetsFiltered,
    pets,
    loading: petsLoading,
    reset: resetPets,
    total: totalPets
  } = useGetPet()

  // Parâmetros da URL
  const queryParams = new URLSearchParams(window.location.search)
  const shortcut = queryParams.get(SHORTCUT_PARAM)
  const shortcutExists = shortcut !== null

  // Verifica o tipo selecionado (tutores ou pets)
  const isTutorType = type === LIST_TYPES.tutor
  const isPetType = type === LIST_TYPES.pet
  const isAnyFilterSet = !!textSearch.length || (isPetType && (!!gender || !!breedType || !!breedId))
  const hasResults = isPetType ? totalPets > 0 : totalTutors > 0

  // Efeito para lidar com a pesquisa por texto
  useEffect(() => {
    setCurrentPage(INITIAL_CURRENT_PAGE)
    if (!isFirstRender && textSearch.trim() !== '') {
      if (isTutorType) {
        handleGetTutor()
      } else if (isPetType) {
        handleGetPet()
      }
    }
  }, [textSearch])

  // Efeito para lidar com filtros de pets (raça, gênero)
  useEffect(() => {
    if (!isFirstRender && isPetType) {
      handleGetPet()
    } else if (isTutorType) {
      handleGetTutor()
    }
  }, [breedType, gender, breedId, currentPage])

  // Efeito para carregar a lista de raças
  useEffect(() => {
    if (!isFirstRender && isPetType) {
      handleLoadBreeds()
    }
  }, [breedType])

  // Efeito para marcar que a primeira renderização ocorreu
  useEffect(() => {
    setIsFirstRender(false)
  }, [])

  // Função para obter tutores com base no texto de pesquisa
  const handleGetTutor = async () => {
    const to = currentPage * LIMIT
    const from = (currentPage - 1) * LIMIT
    await getTutor(textSearch.trim(), from, to)
  }

  // Função para obter pets com base nos filtros
  const handleGetPet = async () => {
    const params = {
      name: textSearch || undefined,
      gender,
      breedType,
      breedId,
      skip: (currentPage - 1) * LIMIT
    }
    await getPetsFiltered(params)
  }

  // Função para carregar a lista de raças com base no tipo de espécie
  const handleLoadBreeds = async () => {
    const breedsList = await getBreeds(breedType)
    setBreedsList(breedsList)
  }

  // Função para lidar com a alteração de texto de pesquisa com debounce
  const handleChangeTextSearch = (value) => {
    debounce(() => setTextSearch(value))
  }

  const handleChangeType = (value) => {
    resetPets()
    resetTutors()
    setTextSearch(INITIAL_TEXT)
    setBreedType(INITIAL_DEFAULT_VALUE)
    setGender(INITIAL_DEFAULT_VALUE)
    setBreedId(INITIAL_DEFAULT_VALUE)
    if (searchInputRef.current) {
      searchInputRef.current.value = INITIAL_TEXT
    }
    setType(value)
  }

  // Função para determinar qual tipo de dados renderizar (tutores, pets ou vazio)
  const dataToRender = (type) => {
    const dataByType = {
      tutors: user,
      pets,
      empty: []
    }
    return isAnyFilterSet ? dataByType[type] : dataByType.empty
  }

  const showNewTutorForm = () => {
    setIsModalVisible(true)
  }

  // Verifica se a página está carregando
  const isPageLoading = petsLoading || tutorsLoading

  return (
    <>
      <SideMenuTemplate>
        <S.MainContainer>
          <S.Main>
            <Alert type='info' show={shortcutExists}>
              <strong>Nova ficha de consulta:</strong> Localize pelo nome do pet ou do tutor para iniciar uma nova ficha. Realize o cadastro do pet caso ele não esteja no sistema.
            </Alert>
            <HeaderPage
              icon={<FileCircleCheck />}
              title={'Tutores e Pets cadastrados'}
              sub={'Acesse as fichas dos tutores e prontuários dos pets'}
              action={
                <S.BoxButton>
                  <S.RegistrationButton onClick={() => showNewTutorForm()}>
                    <PlusIcon />Novo cadastro
                  </S.RegistrationButton>
                </S.BoxButton>
              }
            />
            <S.Actions>
              <S.RadioGroup onChange={(e) => handleChangeType(e.target.value)}>
                <Radio
                  label='Tutores'
                  name='type'
                  value='tutors'
                  defaultChecked={isTutorType}
                />
                <Radio
                  label='Pets'
                  name='type'
                  value='pets'
                  defaultChecked={isPetType}
                />
              </S.RadioGroup>
              <SearchInput
                onChange={e => handleChangeTextSearch(e.target.value)}
                ref={searchInputRef}
                placeholder={isTutorType ? 'Busque pelo nome do Tutor, CPF ou E-mail' : 'Busque pelo nome do Pet'}/>
              {isPetType && (
                <S.SelectGroup>
                  <Select
                    emptyLabel='Todas as espécies'
                    options={PET_TYPES_OPTIONS}
                    name='petType'
                    onChange={(value) => setBreedType(value)}
                  />
                  <Select
                    emptyLabel='Todas as raças'
                    options={PET_BREEDS_OPTIONS(breedsList)}
                    name='petBreed'
                    onChange={(value) => setBreedId(value)}
                    hasTextInput
                  />
                  <Select
                    emptyLabel='Machos e fêmeas'
                    options={PET_GENDER_OPTIONS}
                    name='petGender'
                    onChange={(value) => setGender(value)}
                  />
                </S.SelectGroup>
              )}
              {(isPageLoading || hasResults) && <ResultBox
                resultFor={type}
                data={dataToRender(type)}
                loading={isPageLoading}
                limit={LIMIT}
                currentPage={currentPage}
                totalResults={isPetType ? totalPets : totalTutors}
              />}
              {(!hasResults && isAnyFilterSet && !isPageLoading) &&
              <S.NotFound>
                {NOT_FOUND_ICON}
                <S.NotFoundTitle>{NOT_FOUND_TEXT.title}</S.NotFoundTitle>
                <S.NotFoundSubtitle>{NOT_FOUND_TEXT[type]}</S.NotFoundSubtitle>
              </S.NotFound>
              }
            </S.Actions>
            {isPetType ? (
              <Pagination
                totalItems={totalPets}
                itemsPerPage={LIMIT}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                show={!isPageLoading && hasResults && isAnyFilterSet}
              />
            ) : (
              <TutorPagination
                totalItems={totalTutors}
                itemsPerPage={LIMIT}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                show={!isPageLoading && hasResults && isAnyFilterSet}
              />
            )}
          </S.Main>
        </S.MainContainer>
      </SideMenuTemplate>
      <NewTutorModal visibility={isModalVisible} setVisibility={setIsModalVisible} />
    </>
  )
}

export default TutorsAndPets
